import { FileText, FileSpreadsheet, Image as ImageIcon, BookOpen, Download, ExternalLink } from 'lucide-react'
import { PageHeader, Panel, EmptyState } from '../../ds/index'
import { Icon } from '../../components/Icon'
import { useApi } from '../../lib/useApi'
import { dateShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { fileSize } from '../../components/ClientDocuments'
import type { Summary } from './ClientOverview'

type Doc = {
  id: string; title: string; description: string | null; body: string | null
  file_name: string | null; file_type: string | null; file_size: number | null; pinned: boolean
  created_at: string; updated_at: string; shared: boolean
}

const OPENS = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp'])
const iconFor = (d: { file_type: string | null }) =>
  !d.file_type ? BookOpen : d.file_type.startsWith('image/') ? ImageIcon : /sheet|csv/.test(d.file_type) ? FileSpreadsheet : FileText

/** Guides and files the company shared with this client, or with every client. */
export default function ClientResources() {
  const { data, error } = useApi<{ documents: Doc[] }>('/api/client/documents')
  const summary = useApi<Summary>('/api/client/summary')
  const from = summary.data?.org.name || 'your account team'
  if (error) return <ErrorBlock error={error} />
  if (!data) return <LoadingBlock />
  // The server sorts pinned first, newest next; keep that order.
  const docs = data.documents

  return (
    <>
      <PageHeader title="Resources" subtitle={`Guides and documents from ${from}.`} />
      {docs.length === 0 ? (
        <Panel><EmptyState title="Nothing here yet" body={`When ${from} shares a guide or a document with you, it shows up here.`} /></Panel>
      ) : (
        <div className="doc-grid">
          {docs.map((d) => (
            <article key={d.id} className={d.pinned ? 'doc-card is-pinned' : 'doc-card'}>
              <div className="row-tight" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="doc-icon"><Icon of={iconFor(d)} size={20} /></span>
                <span className="row-tight" style={{ flexWrap: 'wrap' }}>
                  {d.pinned && <span className="pill is-grading">Pinned</span>}
                  {!d.shared && <span className="pill">Just for you</span>}
                </span>
              </div>
              <span className="doc-title">{d.title}</span>
              {d.description && <span className="muted" style={{ overflowWrap: 'anywhere' }}>{d.description}</span>}
              {d.body && (
                <div className="doc-body">
                  {d.body.split(/\n\s*\n/).map((para, i) => <p key={i} style={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{para.trim()}</p>)}
                </div>
              )}
              <div className="doc-foot">
                <span className="muted">Added {dateShort(d.created_at)}</span>
                {d.file_name && (
                  <a className="btn btn-secondary" href={`/api/client/documents/${d.id}/file`}
                     {...(d.file_type && OPENS.has(d.file_type) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                     title={d.file_name}>
                    <Icon of={d.file_type && OPENS.has(d.file_type) ? ExternalLink : Download} size={15} /> {d.file_type && OPENS.has(d.file_type) ? 'Open' : 'Download'} <span className="muted">({fileSize(d.file_size)})</span>
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  )
}
